/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import {
  $SubtenantMemberFilter,
  GetSubtenantMembersData,
  GetSubtenantMembersResponse,
  SubtenantMember,
  SubtenantMemberFilter,
  SubtenantMembersSortableFields,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedSubtenantMembersQueryParams = {
  params: Omit<GetSubtenantMembersData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Custom hook that fetches paginated subtenantMembers with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated subtenantMembers response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function usePaginatedSubtenantMembersQuery({ params, type }: UsePaginatedSubtenantMembersQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return usePaginatedQuery<
    GetSubtenantMembersResponse,
    SubtenantMember,
    SubtenantMemberFilter,
    SubtenantMembersSortableFields,
    GetSubtenantMembersData
  >({
    apiCall: mgmtPlaneAPIClient.subtenants.getSubtenantMembers.bind(mgmtPlaneAPIClient.subtenants),
    filterTypes: jsonSchemaToTypeMap($SubtenantMemberFilter),
    queryKey: 'subtenantMembers',
    realtimeEvents: ['subtenant-membership:updated', 'subtenant:updated'],
    params: {
      tenantId: selectedTenantId!,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
}
